import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { getUserMenuList } from "../../../application/selectors/user";
import { menuMatchesPathSegment } from "../../pages/SLN/shared/projectWorkspace/projectWorkspaceMenu";
import {
  canAccessContractorAssistedRegistration,
  canAccessContractorPortal,
  canManageContractorTeam,
} from "../../utils/authRoleHelpers";
import { Box, CircularProgress, Typography } from "@mui/material";

/**
 * لیست مسیرهایی که نیاز به بررسی دسترسی ندارند (Public Pages)
 */
const PUBLIC_PATHS = [
  "/",
  "/home",
  "/profile",
  "/account/profile",
  "/account/reset-password",
  "/403",
  "/404",
  "/error",
];

const CONTRACTOR_PORTAL_FIRST_SEGMENTS = new Set([
  "dashboard",
  "projects",
  "contracts",
  "payments",
  "profile",
  "contractorportal",
]);

const normalizePath = (pathname) =>
  (pathname || "").toLowerCase().replace(/\/+$/, "");

const isContractorRoleBypassPath = (pathname) => {
  const p = normalizePath(pathname);
  if (p.startsWith("/sln/contractorportal") && canAccessContractorPortal()) {
    return true;
  }
  if (p === "/sln/contractor" && canAccessContractorPortal()) {
    return true;
  }
  if (p.startsWith("/sln/contractor/")) {
    const first = p.slice("/sln/contractor/".length).split("/")[0];
    if (CONTRACTOR_PORTAL_FIRST_SEGMENTS.has(first) && canAccessContractorPortal()) {
      return true;
    }
    if (
      (first === "assistedregistration") &&
      canAccessContractorAssistedRegistration()
    ) {
      return true;
    }
    if (first === "team" && canManageContractorTeam()) {
      return true;
    }
  }
  return false;
};
/**
 * بررسی اینکه آیا یک path در لیست public است یا نه
 */
const isPublicPath = (pathname) => {
  return PUBLIC_PATHS.some(publicPath => {
    if (publicPath === "/") {
      return pathname === "/" || pathname === "";
    }
    return pathname.toLowerCase().startsWith(publicPath.toLowerCase());
  });
};

/**
 * ProtectedRoute - محافظت از صفحات بر اساس دسترسی کاربر
 * 
 * این component از menuList کاربر استفاده می‌کند تا بررسی کند که آیا کاربر
 * به مسیر فعلی دسترسی دارد یا نه.
 * 
 * @param {React.ReactNode} children - محتوای صفحه
 * @param {string} requiredPath - مسیر مورد نیاز برای بررسی دسترسی (اختیاری)
 * @param {boolean} checkAccess - آیا دسترسی چک شود یا نه (پیش‌فرض: true)
 */
const ProtectedRoute = ({ children, requiredPath = null, checkAccess = true }) => {
  const location = useLocation();
  const menuList = useSelector(getUserMenuList);
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  // محاسبه path بدون side effect
  const pathToCheck = requiredPath || location.pathname;
  const isPublic = isPublicPath(pathToCheck);

  useEffect(() => {
    // اگر صفحه public است، بلافاصله دسترسی بده
    if (isPublic) {
      setHasAccess(true);
      setIsChecking(false);
      return;
    }

    if (isContractorRoleBypassPath(pathToCheck)) {
      setHasAccess(true);
      setIsChecking(false);
      return;
    }

    // اگر نیازی به چک کردن دسترسی نیست
    if (!checkAccess) {
      setHasAccess(true);
      setIsChecking(false);
      return;
    }

    // اگر menuList هنوز لود نشده
    if (!menuList || menuList.length === 0) {
      // کمی صبر می‌کنیم تا menuList لود شود
      const timer = setTimeout(() => {
        setIsChecking(false);
        // اگر بعد از 2 ثانیه هنوز menuList نیامده، اجازه دسترسی نمی‌دهیم
        setHasAccess(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }

    // بررسی دسترسی
    const pathnames = pathToCheck.split("/").filter((x) => x);
    
    // اگر path خالی است (صفحه اصلی)، دسترسی بده
    if (pathnames.length === 0) {
      setHasAccess(true);
      setIsChecking(false);
      return;
    }
    
    const menuItem = findMenuItemByPath(pathnames, { child: menuList, level: 1 });
    const hasPageAccess = checkUserAccess(pathnames, menuList);
    
    console.log("🔒 ProtectedRoute Check:", {
      path: pathToCheck,
      pathnames,
      menuItem: menuItem,
      menuItemLevel: menuItem?.level,
      actionAccess: menuItem?.actionAccess,
      hasAccess: hasPageAccess,
      menuListCount: menuList.length
    });
    
    setHasAccess(hasPageAccess);
    setIsChecking(false);
  }, [menuList, location.pathname, requiredPath, checkAccess, pathToCheck, isPublic]);

  // تابع بررسی دسترسی کاربر به یک مسیر
  const checkUserAccess = (pathnames, menuList) => {
    if (!pathnames || pathnames.length === 0) return false;
    
    // پیدا کردن menu item مربوطه
    const menuItem = findMenuItemByPath(pathnames, { child: menuList, level: 1 });
    
    if (!menuItem) {
      // اگر menu item پیدا نشد، دسترسی ندارد
      console.warn("⚠️ Menu item not found for path:", pathnames);
      return false;
    }

    // اگر پیدا کرد اما همان menuList اصلی است (یعنی path match نشده)
    if (menuItem === menuList || menuItem.level === 1) {
      console.warn("⚠️ Path not fully matched:", pathnames);
      return false;
    }

    // بررسی دسترسی read (view)
    // اگر actionAccess null یا undefined است، به معنی دسترسی کامل است
    if (menuItem.actionAccess === null || menuItem.actionAccess === undefined) {
      return true;
    }

    // اگر actionAccess وجود دارد، بررسی می‌کنیم که read access دارد یا نه
    const hasReadAccess = menuItem.actionAccess?.read === true;
    
    return hasReadAccess;
  };

  // تابع پیدا کردن menu item بر اساس path
  // این تابع مشابه onFindSpecificNode در useCheckPermission است
  const findMenuItemByPath = (pathnames, menuList) => {
    let specificNode = menuList;

    pathnames.forEach((path) => {
      let item = null;
      
      if (specificNode.level === 1) {
        // Level 1: ابتدا در فرزندان مستقیم (ماژول‌های سطح ۰ مثل SLN, UM, BI) جستجو کن
        item = specificNode.child.find((menu) =>
          menuMatchesPathSegment(menu, path)
        );
        if (!item) {
          let child = [];
          for (let i = 0; i < specificNode.child.length; i++) {
            if (specificNode.child[i]?.child) {
              child = [...child, ...specificNode.child[i].child];
            }
          }
          item = child.find((menu) => menuMatchesPathSegment(menu, path));
        }
      } else {
        // سایر level ها: جستجو در child های node فعلی
        if (Array.isArray(specificNode?.child)) {
          item = specificNode.child.find((menu) =>
            menuMatchesPathSegment(menu, path)
          );
        }
      }

      if (item) {
        specificNode = item;
      }
      // اگر item پیدا نشد، specificNode همان می‌ماند
    });

    return specificNode;
  };

  // در حال بررسی دسترسی
  if (isChecking) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          در حال بررسی دسترسی...
        </Typography>
      </Box>
    );
  }

  // اگر دسترسی ندارد
  if (!hasAccess) {
    return (
      <Navigate 
        to="/403" 
        state={{ 
          from: location,
          message: "شما به این صفحه دسترسی ندارید" 
        }} 
        replace 
      />
    );
  }

  // اگر دسترسی دارد، محتوا را نمایش بده
  return children;
};

export default ProtectedRoute;
